import { resolveField, VerificationFieldConfig } from '../types/types';
import { normalizeCountryCode } from 'src/app/shared/country-codes';
import { bankReadyForCountry } from 'src/app/shared/bank-required-fields';
import { CacheVerificationType } from './verification-cache.models';

export const BANK_SUPPORTED_COUNTRIES = new Set([
  'BE',
  'BR',
  'CN',
  'FR',
  'GB',
  'ID',
  'IN',
  'IT',
  'NL',
  'US',
  'VN',
  'AR',
  'BD',
  'KR',
  'MY',
  'MX',
  'NP',
  'NG',
  'PK',
  'PE',
  'PL',
  'SA',
  'ZA',
  'TH',
  'TR',
  'UG',
  'UY',
  'AE',
]);

export interface DetectedField {
  readonly key: string;
  readonly type: CacheVerificationType;
  readonly panelIndex: number;
  readonly countryCode: string;
  readonly fieldData: Record<string, unknown>;
}

function nonEmpty(v: unknown): boolean {
  return v != null && (typeof v === 'string' ? v : '').trim() !== '';
}

function normalizeCountry(v: unknown): string {
  return normalizeCountryCode(v);
}

function ibanCountryCode(raw: unknown): string {
  const s = (typeof raw === 'string' ? raw : '').replaceAll(/\s+/g, '').trim().toUpperCase();
  return /^[A-Z]{2}/.test(s) ? s.slice(0, 2) : '';
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

interface FieldSets {
  bankFields: Record<string, string[]>;
  tinFields: Record<string, string[]>;
  gstFields: Record<string, string[]>;
  euVatFields: Record<string, string[]>;
  ibanField: string[];
  bankCountries: Set<string>;
  tinCountries: Set<string>;
  gstCountries: Set<string>;
  euVatCountries: Set<string>;
  emailFields: string[];
}

function tryDetectBank(
  data: Record<string, unknown>,
  panelIndex: number,
  bankFields: Record<string, string[]>,
  bankCountries: Set<string>
): DetectedField | null {
  const bankCC = normalizeCountry(resolveField(data, bankFields['country'] ?? []));
  if (!bankCC || (!bankCountries.has('ALL') && !bankCountries.has(bankCC))) return null;
  if (!nonEmpty(resolveField(data, bankFields['holderName'] ?? []))) return null;
  if (!nonEmpty(resolveField(data, bankFields['accountNumber'] ?? []))) return null;
  if (
    !bankReadyForCountry(bankCC, {
      clearing: resolveField(data, bankFields['clearingSystemId'] ?? []),
      creditor: resolveField(data, bankFields['creditorIdentifier'] ?? []),
      creditorType: resolveField(data, bankFields['creditorIdentifierType'] ?? []),
    })
  )
    return null;
  return { key: `bank:${panelIndex}`, type: 'bank', panelIndex, countryCode: bankCC, fieldData: data };
}

function tryDetectTin(
  data: Record<string, unknown>,
  panelIndex: number,
  tinFields: Record<string, string[]>,
  tinCountries: Set<string>
): DetectedField | null {
  const tinCC = normalizeCountry(resolveField(data, tinFields['country'] ?? []));
  if (!tinCC || (!tinCountries.has('ALL') && !tinCountries.has(tinCC))) return null;
  if (!nonEmpty(resolveField(data, tinFields['tin'] ?? []))) return null;
  if (!nonEmpty(resolveField(data, tinFields['name'] ?? []))) return null;
  return { key: `tin:${panelIndex}`, type: 'tin', panelIndex, countryCode: tinCC, fieldData: data };
}

function tryDetectIban(data: Record<string, unknown>, panelIndex: number, ibanField: string[]): DetectedField | null {
  const ibanVal = resolveField(data, ibanField);
  if (!nonEmpty(ibanVal)) return null;
  return {
    key: `iban:${panelIndex}`,
    type: 'iban',
    panelIndex,
    countryCode: ibanCountryCode(ibanVal),
    fieldData: data,
  };
}

const EMAIL_DOMAIN_TYPE: CacheVerificationType = 'email_domain';

export function emailDomainKey(email: string): string {
  return `${EMAIL_DOMAIN_TYPE}:${email}`;
}

export function parseEmailDomainKey(key: string): string {
  return key.slice(EMAIL_DOMAIN_TYPE.length + 1);
}

function tryDetectEmail(key: string, value: unknown, emailFields: string[]): DetectedField | null {
  if (!emailFields.includes(key)) return null;
  const email = typeof value === 'string' ? value.trim() : '';
  if (!nonEmpty(email)) return null;
  return {
    key: emailDomainKey(email),
    type: EMAIL_DOMAIN_TYPE,
    panelIndex: -1,
    countryCode: '',
    fieldData: { emailAddress: email },
  };
}

function detectEmailFields(mergedData: Record<string, unknown>, emailFields: string[]): DetectedField[] {
  const seen = new Set<string>();
  const results: DetectedField[] = [];
  for (const [key, value] of Object.entries(mergedData)) {
    const f = tryDetectEmail(key, value, emailFields);
    if (f && !seen.has(f.key)) {
      seen.add(f.key);
      results.push(f);
    }
  }
  return results;
}

function tryDetectGst(
  data: Record<string, unknown>,
  panelIndex: number,
  gstFields: Record<string, string[]>,
  gstCountries: Set<string>
): DetectedField | null {
  const gstCC = normalizeCountry(resolveField(data, gstFields['country'] ?? []));
  if (!gstCC || (!gstCountries.has('ALL') && !gstCountries.has(gstCC))) return null;
  if (!nonEmpty(resolveField(data, gstFields['gstNumber'] ?? []))) return null;
  if (!nonEmpty(resolveField(data, gstFields['businessName'] ?? []))) return null;
  if (!nonEmpty(resolveField(data, gstFields['transactionDate'] ?? []))) return null;
  return { key: `gst:${panelIndex}`, type: 'gst', panelIndex, countryCode: gstCC, fieldData: data };
}

/**
 * Northern Ireland suppliers pick "United Kingdom" as their country, never "Northern Ireland", so a
 * GB country with an XI-prefixed VAT number is checked as XI. Other UK numbers are not on Signzy's list.
 */
function euVatCountryCode(countryCode: string, vatNumber: string): string {
  if (countryCode !== 'GB') return countryCode;
  return vatNumber.replaceAll(/\s+/g, '').toUpperCase().startsWith('XI') ? 'XI' : countryCode;
}

function tryDetectEuVat(
  data: Record<string, unknown>,
  panelIndex: number,
  euVatFields: Record<string, string[]>,
  euVatCountries: Set<string>
): DetectedField | null {
  const vatNumber = resolveField(data, euVatFields['vatNumber'] ?? []);
  if (typeof vatNumber !== 'string' || !nonEmpty(vatNumber)) return null;
  const vatCC = euVatCountryCode(normalizeCountry(resolveField(data, euVatFields['country'] ?? [])), vatNumber);
  if (!vatCC || (!euVatCountries.has('ALL') && !euVatCountries.has(vatCC))) return null;
  return { key: `eu_vat:${panelIndex}`, type: 'eu_vat', panelIndex, countryCode: vatCC, fieldData: data };
}

function collectFromRow(
  row: unknown,
  panelIndex: number,
  {
    bankFields,
    tinFields,
    gstFields,
    euVatFields,
    ibanField,
    bankCountries,
    tinCountries,
    gstCountries,
    euVatCountries,
  }: FieldSets,
  hasBankCfg: boolean,
  hasTinCfg: boolean,
  hasGstCfg: boolean,
  hasIbanCfg: boolean
): DetectedField[] {
  if (!isRecord(row)) return [];
  const out: DetectedField[] = [];
  if (hasBankCfg) {
    const f = tryDetectBank(row, panelIndex, bankFields, bankCountries);
    if (f) out.push(f);
  }
  if (hasTinCfg) {
    const f = tryDetectTin(row, panelIndex, tinFields, tinCountries);
    if (f) out.push(f);
  }
  if (hasGstCfg) {
    const f = tryDetectGst(row, panelIndex, gstFields, gstCountries);
    if (f) out.push(f);
  }
  // Without an EU_VAT config there are no countries to check, so this finds nothing.
  const euVat = tryDetectEuVat(row, panelIndex, euVatFields, euVatCountries);
  if (euVat) out.push(euVat);
  if (hasIbanCfg) {
    const f = tryDetectIban(row, panelIndex, ibanField);
    if (f) out.push(f);
  }
  return out;
}

function detectFromPanelRows(
  mergedData: Record<string, unknown>,
  sets: FieldSets,
  hasBankCfg: boolean,
  hasTinCfg: boolean,
  hasGstCfg: boolean,
  hasIbanCfg: boolean
): DetectedField[] {
  const results: DetectedField[] = [];
  for (const val of Object.values(mergedData)) {
    if (!Array.isArray(val)) continue;
    for (let i = 0; i < val.length; i++) {
      for (const f of collectFromRow(val[i], i, sets, hasBankCfg, hasTinCfg, hasGstCfg, hasIbanCfg)) {
        results.push(f);
      }
    }
  }
  return results;
}

function detectFromFlatFields(mergedData: Record<string, unknown>, sets: FieldSets): DetectedField[] {
  const results: DetectedField[] = [];
  const f1 = tryDetectBank(mergedData, -1, sets.bankFields, sets.bankCountries);
  if (f1) results.push(f1);
  const f2 = tryDetectTin(mergedData, -1, sets.tinFields, sets.tinCountries);
  if (f2) results.push(f2);
  const f3 = tryDetectGst(mergedData, -1, sets.gstFields, sets.gstCountries);
  if (f3) results.push(f3);
  const f4 = tryDetectEuVat(mergedData, -1, sets.euVatFields, sets.euVatCountries);
  if (f4) results.push(f4);
  const f5 = tryDetectIban(mergedData, -1, sets.ibanField);
  if (f5) results.push(f5);
  return results;
}

/** Coerces raw field-mapping rows into VerificationFieldConfig, defaulting missing parts so lookups never throw. */
export function toVerificationFieldConfig(raw: readonly unknown[]): VerificationFieldConfig[] {
  return raw.filter(isRecord).map((c) => ({
    type: typeof c['type'] === 'string' ? c['type'] : '',
    anchorField: typeof c['anchorField'] === 'string' ? c['anchorField'] : '',
    countries: Array.isArray(c['countries']) ? (c['countries'] as string[]) : [],
    fields: isRecord(c['fields']) ? (c['fields'] as Record<string, string[]>) : {},
  }));
}

/** Cache keys of the emails the auto-trigger effect will actually verify for this data. */
export function verifiableEmailKeys(
  mergedData: Record<string, unknown>,
  fieldConfig: VerificationFieldConfig[]
): Set<string> {
  const keys = detectVerifiableFields(mergedData, fieldConfig)
    .filter((f) => f.type === EMAIL_DOMAIN_TYPE)
    .map((f) => f.key);
  return new Set(keys);
}

export function detectVerifiableFields(
  mergedData: Record<string, unknown>,
  fieldConfig: VerificationFieldConfig[]
): DetectedField[] {
  const bankCfg = fieldConfig.find((c) => c.type.toUpperCase() === 'BANK');
  const tinCfg = fieldConfig.find((c) => c.type.toUpperCase() === 'TIN');
  const gstCfg = fieldConfig.find((c) => c.type.toUpperCase() === 'GST');
  const euVatCfg = fieldConfig.find((c) => c.type.toUpperCase() === 'EU_VAT');
  const ibanCfg = fieldConfig.find((c) => c.type.toUpperCase() === 'IBAN');
  const emailCfg = fieldConfig.find((c) => c.type.toUpperCase() === 'EMAIL_DOMAIN');

  const sets: FieldSets = {
    bankFields: bankCfg?.fields ?? {},
    tinFields: tinCfg?.fields ?? {},
    gstFields: gstCfg?.fields ?? {},
    euVatFields: euVatCfg?.fields ?? {},
    ibanField: ibanCfg ? ibanCfg.fields[ibanCfg.anchorField] ?? [] : ['ibanNumber'],
    bankCountries: bankCfg ? new Set(bankCfg.countries) : BANK_SUPPORTED_COUNTRIES,
    tinCountries: tinCfg ? new Set(tinCfg.countries) : new Set(['US']),
    gstCountries: gstCfg ? new Set(gstCfg.countries) : new Set(['CA']),
    euVatCountries: new Set(euVatCfg?.countries),
    emailFields: emailCfg ? emailCfg.fields[emailCfg.anchorField] ?? [] : [],
  };

  const panelResults = detectFromPanelRows(mergedData, sets, !!bankCfg, !!tinCfg, !!gstCfg, !!ibanCfg);
  const emailResults = emailCfg ? detectEmailFields(mergedData, sets.emailFields) : [];
  return [...panelResults, ...detectFromFlatFields(mergedData, sets), ...emailResults];
}

export function mergeRequestFormData(request: {
  internalData?: Record<string, unknown> | null;
  externalData?: Record<string, unknown> | null;
  notesData?: Record<string, unknown> | null;
  [key: string]: unknown;
}): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  const assign = (src: unknown) => {
    if (isRecord(src)) Object.assign(out, src);
  };

  assign(request.externalData);
  assign(request.notesData);
  assign(request.internalData);

  return out;
}
